import { createGUID, timeQuantum } from "@/utils/tools";
import { chunk, chunk2, cancel, merge, merge2 } from "@/api/upload";
import { ElLoading } from "element-plus";

const MODULES = [
  "LifeVideo",
  "TutorAudio",
  "LifePhoto",
  "UserFace",
  "CoursewareResources",
  "TutorCertificates",
  "Absent",
  "IdentityImg",
  "BankStatement",
  "ElectronicallySigned",
  "ProblemScreenshot",
  "Attachment",
  "AppealFile",
  "LeaveReason",
  "TeacherCertification",
  "PoliceCertificate",
  "TesolCertificateSelf",
  "ProfessionalIdPhoto",
  "GroupPhoto",
  "ToturTaskFile",
  "SubjectWrong",
  "LessonUserFaceFile",
  "TutorEquipmentPhoto",
  "TeachingQualityInspection",
];

function createProgressbar() {
  let loading = null;
  return {
    registerEvent() {},
    open(completed) {
      if (!loading) {
        loading = ElLoading.service({
          lock: true,
          text: `Uploading ${completed || 1}%…`,
          background: "rgba(0, 0, 0, 0.35)",
        });
      } else {
        loading.setText(`Uploading ${completed}%…`);
      }
      if (completed >= 100 && loading) {
        loading.close();
        loading = null;
      }
    },
    cancel() {
      if (loading) {
        loading.close();
        loading = null;
      }
    },
  };
}

function createUploader(opt) {
  const option = Object.assign(
    {
      chunkSize: 0.5 * 1024 * 1024,
      module: "",
      progressbar: createProgressbar(),
      useChunk2: false,
    },
    opt
  );
  const chunkApi = option.useChunk2 ? chunk2 : chunk;
  const mergeApi = option.useChunk2 ? merge2 : merge;

  let moduleName = option.module;
  if (MODULES.indexOf(moduleName) === -1) {
    throw new Error(`Upload module [${moduleName}] does not exist`);
  }

  let currentChunk = 0;
  let totalChunk = 1;
  let file = null;
  let fileCode = "";
  let chunked = 0;
  let totalChunkSize = 0;
  let mergeShow = true;
  let remainingTime = 5 * 60;

  const display = (completed) => {
    let pct = completed;
    if (!pct) {
      pct = Math.floor((chunked / totalChunkSize) * 100);
    }
    if (pct === 0) pct = 1;
    if (pct > 100) pct = 100;
    option.progressbar.open(pct, remainingTime);
  };

  const fileMerge = async () => {
    if (mergeShow) display(99);
    const formdata = new FormData();
    formdata.append("fileCode", fileCode);
    formdata.append("moduleName", moduleName);
    const res = await mergeApi(formdata);
    const resultObj = res.data;
    if (resultObj.ErrorCode === 0) {
      if (mergeShow) display(100);
      resultObj.Body.FileName = file.name;
    }
    return resultObj;
  };

  const error = () => {
    option.progressbar.cancel();
  };

  const setChunkTime = (lastTime) => {
    const chunkUploadTime = timeQuantum(lastTime);
    remainingTime = chunkUploadTime * (totalChunk - currentChunk);
  };

  const chunkUpload = async () => {
    if (!file) {
      error();
      return { ErrorCode: 100, Msg: "Please select a file first." };
    }
    display();
    const chunkFile = file.slice(
      currentChunk * option.chunkSize,
      (currentChunk + 1) * option.chunkSize
    );
    if (!chunkFile) {
      error();
      return { ErrorCode: 100, Msg: "Upload failed, please try again later." };
    }
    chunked += file.size < option.chunkSize ? file.size : option.chunkSize;
    const lastTime = new Date();
    const renameFile = new File([chunkFile], file.name, { type: file.type });
    const formdata = new FormData();
    formdata.append("file", renameFile);
    formdata.append("chunk", currentChunk + 1);
    formdata.append("fileCode", fileCode);
    formdata.append("fileName", file.name);
    formdata.append("moduleName", moduleName);
    const res = await chunkApi(formdata);
    setChunkTime(lastTime);
    const resultObj = res.data;
    if (resultObj.ErrorCode === 0) {
      currentChunk++;
      if (currentChunk < totalChunk) {
        return await chunkUpload();
      }
      return await fileMerge();
    }
    return resultObj;
  };

  const init = (f) => {
    file = f;
    currentChunk = 0;
    fileCode = createGUID();
    remainingTime = 5 * 60;
    totalChunk = Math.ceil(file.size / option.chunkSize);
  };

  const uploadBatchWithKey = async (filesData) => {
    let list = filesData;
    if (filesData instanceof FileList) {
      list = [{ key: "DEFAULTKEY", file: filesData }];
    } else if (filesData instanceof Array) {
      list = filesData
        .map((t) => {
          if (t.file instanceof FileList || t.file instanceof Array) {
            const files = [];
            for (let i = 0; i < t.file.length; i++) {
              files.push({ key: t.key, file: t.file[i] });
            }
            return files;
          }
          return [t];
        })
        .reduce((prev, next) => prev.concat(next), []);
    }

    const datas = {};
    mergeShow = false;
    totalChunkSize = 0;
    for (let i = 0; i < list.length; i++) {
      totalChunkSize += list[i].file.size;
    }
    for (let i = 0; i < list.length; i++) {
      init(list[i].file);
      if (list[i].module) moduleName = list[i].module;
      if (i === list.length - 1) mergeShow = true;
      chunked = 0;
      const data = await chunkUpload();
      if (!data || data.ErrorCode !== 0) {
        error();
        return data;
      }
      datas[list[i].key] = datas[list[i].key] || [];
      datas[list[i].key].push(data.Body);
    }
    return { ErrorCode: 0, Body: datas };
  };

  const upload = async (f) => {
    init(f);
    chunked = 0;
    mergeShow = true;
    totalChunkSize = f.size;
    const result = await chunkUpload();
    if (result.ErrorCode !== 0) {
      error();
    }
    return result;
  };

  option.progressbar.registerEvent(async () => {
    file = null;
    currentChunk = 0;
    await cancel(fileCode, moduleName);
  });

  return { upload, uploadBatchWithKey };
}

export function uploader(opt) {
  return createUploader(opt);
}
